import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  getGroupsByUser,
  getGroupAdherents,
} from "../../../../services/groupServices";

import {
  calculateAssiduity,
  clearOneAttendance,
  getAttendancesByGroup,
  setOneAttendance,
} from "../../../../services/attendanceServices";

import {
  buildDailyAttendanceMap,
  buildMonthlyAttendanceRows,
  daysArray,
  getApprovedGroupAdherents,
  getGroupAdherentsCount,
  getLocalDateInput,
  getMonthInputValue,
  getGroupName,
  isBirthdayToday,
  normalizeAttendancesResponse,
  normalizeGroupAdherentsResponse,
  normalizeText,
  sortAdherentsByName,
} from "../utils/attendanceUtils";

const DEFAULT_FILTERS = {
  search: "",
  selectedDate: getLocalDateInput(),
  selectedGroupId: "",
  onlyPresent: false,
  onlyAbsent: false,
  onlyBirthday: false,
  sortAssiduity: false,
};

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getCurrentUserId(user, storedUser) {
  return (
    user?.id ||
    user?.userId ||
    user?.user_id ||
    storedUser?.id ||
    storedUser?.userId ||
    storedUser?.user_id ||
    null
  );
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0,
  );
}

function normalizeGroupsResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.groups)) return response.groups;
  if (Array.isArray(response?.Groups)) return response.Groups;
  if (Array.isArray(response?.data)) return response.data;

  return [];
}

function normalizeUserGroupsResponse(response) {
  const rawGroups = normalizeGroupsResponse(response);

  return rawGroups
    .map((item) => {
      if (item?.Group) return item.Group;
      if (item?.group) return item.group;
      return item;
    })
    .filter((group) => group?.id);
}

function sortGroupsByName(groups = []) {
  return groups
    .slice()
    .sort((a, b) => getGroupName(a).localeCompare(getGroupName(b), "fr"));
}

function getMonthlyCellKey(adherentId, day) {
  return `${adherentId}-${day}`;
}

function replaceMonthlyCellInList(
  list = [],
  { selectedGroupId, adherentId, dateISO, statusValue },
) {
  const filtered = (list || []).filter((attendance) => {
    const sameAdherent = Number(attendance?.adherent_id) === Number(adherentId);
    const sameDate =
      String(attendance?.date_presence || "").slice(0, 10) === dateISO;

    return !(sameAdherent && sameDate);
  });

  if (statusValue === 0) {
    return filtered;
  }

  return [
    ...filtered,
    {
      group_id: Number(selectedGroupId),
      adherent_id: Number(adherentId),
      date_presence: dateISO,
      presence: statusValue === 1,
    },
  ];
}

export function useAttendanceDashboard({ user, addNotification }) {
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);
  const userId = getCurrentUserId(user, storedUser);

  const ATTENDANCE_ALLOWED_ROLES = [1, 5, 6, 7];

  const authorized = ATTENDANCE_ALLOWED_ROLES.includes(roleId);

  const [groups, setGroups] = useState([]);
  const [groupAdherents, setGroupAdherents] = useState([]);

  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);

  const [attendance, setAttendance] = useState({});
  const attendanceRef = useRef(attendance);

  const [assiduityInfo, setAssiduityInfo] = useState({});
  const assiduityInfoRef = useRef(assiduityInfo);

  const [savingIds, setSavingIds] = useState(new Set());

  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingAdherents, setLoadingAdherents] = useState(false);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const selectedDate = filters.selectedDate || getLocalDateInput();
  const selectedGroupId = filters.selectedGroupId;

  const [groupCounts, setGroupCounts] = useState({});

  /* ===== Tableau mensuel ===== */

  const [selectedMonth, setSelectedMonthState] = useState(getMonthInputValue());
  const [monthlyAttendances, setMonthlyAttendances] = useState([]);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [savingMonthCells, setSavingMonthCells] = useState(new Set());

  const selectedGroup = useMemo(() => {
    if (!selectedGroupId) return null;

    return (
      groups.find((group) => String(group.id) === String(selectedGroupId)) ||
      null
    );
  }, [groups, selectedGroupId]);

  const groupAdherentIdsKey = useMemo(() => {
    return groupAdherents.map((adherent) => adherent.id).join("|");
  }, [groupAdherents]);

  useEffect(() => {
    attendanceRef.current = attendance;
  }, [attendance]);

  useEffect(() => {
    assiduityInfoRef.current = assiduityInfo;
  }, [assiduityInfo]);

  useEffect(() => {
    const month = String(selectedDate || "").slice(0, 7);

    if (month) {
      setSelectedMonthState(month);
    }
  }, [selectedDate]);

  useEffect(() => {
    setMonthlyRows(
      buildMonthlyAttendanceRows(
        selectedMonth,
        groupAdherents,
        monthlyAttendances,
      ),
    );
  }, [groupAdherents, monthlyAttendances, selectedMonth]);

  const setFilters = useCallback((updater) => {
    setFiltersState((previous) => {
      const next =
        typeof updater === "function"
          ? updater(previous)
          : { ...previous, ...updater };

      return {
        ...DEFAULT_FILTERS,
        ...previous,
        ...next,
        selectedDate: next.selectedDate || getLocalDateInput(),
      };
    });
  }, []);

  const setSelectedMonth = useCallback((value) => {
    setSelectedMonthState(value || getMonthInputValue());
  }, []);

  const setSavingForId = useCallback((adherentId, value) => {
    setSavingIds((previous) => {
      const next = new Set(previous);

      if (value) next.add(String(adherentId));
      else next.delete(String(adherentId));

      return next;
    });
  }, []);

  const setSavingForMonthCell = useCallback((adherentId, day, value) => {
    setSavingMonthCells((previous) => {
      const next = new Set(previous);
      const key = getMonthlyCellKey(adherentId, day);

      if (value) next.add(key);
      else next.delete(key);

      return next;
    });
  }, []);

  const replaceMonthlyCell = useCallback(
    ({ dateISO, adherentId, statusValue }) => {
      if (!selectedMonth || !String(dateISO || "").startsWith(selectedMonth)) {
        return;
      }

      setMonthlyAttendances((previous) =>
        replaceMonthlyCellInList(previous, {
          selectedGroupId,
          adherentId,
          dateISO,
          statusValue,
        }),
      );
    },
    [selectedGroupId, selectedMonth],
  );

  const fetchGroupCounts = useCallback(async (groupsToCount = []) => {
    const safeGroups = Array.isArray(groupsToCount) ? groupsToCount : [];

    if (!safeGroups.length) {
      setGroupCounts({});
      return;
    }

    try {
      const entries = await Promise.all(
        safeGroups.map(async (group) => {
          if (!group?.id) return null;

          const directCount = getGroupAdherentsCount(group);

          if (directCount > 0) {
            return [String(group.id), directCount];
          }

          try {
            const response = await getGroupAdherents(group.id);
            const loadedAdherents = normalizeGroupAdherentsResponse(response);

            const approvedAdherents = loadedAdherents.filter((adherent) => {
              const status =
                adherent?.AdherentGroup?.status ||
                adherent?.adherentGroup?.status ||
                adherent?.adherentgroup?.status ||
                adherent?.status ||
                "";

              if (!status) return true;

              return status === "approved";
            });

            return [String(group.id), approvedAdherents.length];
          } catch (error) {
            console.warn(
              `Impossible de charger le compteur du groupe #${group.id}`,
              error,
            );

            return [String(group.id), directCount];
          }
        }),
      );

      const nextCounts = entries.reduce((acc, entry) => {
        if (!entry) return acc;

        const [groupId, count] = entry;
        acc[groupId] = Number(count || 0);

        return acc;
      }, {});

      setGroupCounts(nextCounts);
    } catch (error) {
      console.error("Erreur chargement compteurs groupes :", error);
      setGroupCounts({});
    }
  }, []);

  const fetchGroups = useCallback(
    async ({ silent = true } = {}) => {
      if (!authorized || !userId) {
        setGroups([]);
        setLoadingGroups(false);
        return;
      }

      if (!silent) setRefreshing(true);
      setLoadingGroups(true);

      try {
        const response = await getGroupsByUser(userId);
        const safeGroups = sortGroupsByName(
          normalizeUserGroupsResponse(response),
        );

        setGroups(safeGroups);
        fetchGroupCounts(safeGroups);

        if (!silent) {
          addNotification?.("Groupes actualisés.", "success");
        }
      } catch (error) {
        console.error("Erreur chargement groupes coach :", error);
        addNotification?.("Erreur lors du chargement de vos groupes.", "error");
        setGroups([]);
      } finally {
        setLoadingGroups(false);
        setRefreshing(false);
      }
    },
    [addNotification, authorized, fetchGroupCounts, userId],
  );

  const fetchGroupAdherents = useCallback(async () => {
    if (!selectedGroupId) {
      setGroupAdherents([]);
      setAttendance({});
      setMonthlyAttendances([]);
      attendanceRef.current = {};
      return;
    }

    const currentGroup =
      groups.find((group) => String(group.id) === String(selectedGroupId)) ||
      null;

    if (!currentGroup) {
      setGroupAdherents([]);
      setAttendance({});
      setMonthlyAttendances([]);
      attendanceRef.current = {};
      return;
    }

    setLoadingAdherents(true);

    try {
      const embeddedAdherents = getApprovedGroupAdherents(currentGroup);

      if (embeddedAdherents.length > 0) {
        const sorted = sortAdherentsByName(embeddedAdherents);

        setGroupAdherents(sorted);
        setGroupCounts((previous) => ({
          ...previous,
          [String(currentGroup.id)]: sorted.length,
        }));

        return;
      }

      const response = await getGroupAdherents(currentGroup.id);
      const loadedAdherents = normalizeGroupAdherentsResponse(response);
      const sorted = sortAdherentsByName(loadedAdherents);

      setGroupAdherents(sorted);
      setGroupCounts((previous) => ({
        ...previous,
        [String(currentGroup.id)]: sorted.length,
      }));
    } catch (error) {
      console.error("Erreur chargement judokas du groupe :", error);
      addNotification?.(
        "Erreur lors du chargement des judokas du groupe.",
        "error",
      );
      setGroupAdherents([]);
    } finally {
      setLoadingAdherents(false);
    }
  }, [addNotification, groups, selectedGroupId]);

  const fetchAttendanceForDay = useCallback(async () => {
    if (!selectedGroupId || !groupAdherents.length) {
      setAttendance({});
      attendanceRef.current = {};
      return;
    }

    setLoadingAttendance(true);

    try {
      const response = await getAttendancesByGroup(selectedGroupId, {
        date: selectedDate,
      });

      const safeAttendances = normalizeAttendancesResponse(response);
      const ids = groupAdherents.map((adherent) => adherent.id);

      const map = buildDailyAttendanceMap(selectedDate, ids, safeAttendances);

      setAttendance(map);
      attendanceRef.current = map;
    } catch (error) {
      console.error("Erreur chargement appel du jour :", error);
      addNotification?.("Erreur lors du chargement de l'appel.", "error");
    } finally {
      setLoadingAttendance(false);
    }
  }, [addNotification, groupAdherents, selectedDate, selectedGroupId]);

  const fetchAttendanceForMonth = useCallback(async () => {
    if (!selectedGroupId || !groupAdherents.length || !selectedMonth) {
      setMonthlyAttendances([]);
      return;
    }

    setLoadingMonthly(true);

    try {
      const days = daysArray(selectedMonth);

      const results = await Promise.all(
        days.map(async (day) => {
          const date = `${selectedMonth}-${day}`;

          try {
            const response = await getAttendancesByGroup(selectedGroupId, {
              date,
            });

            return normalizeAttendancesResponse(response);
          } catch (error) {
            console.warn(`Erreur chargement présence du ${date}`, error);
            return [];
          }
        }),
      );

      const flattened = results.flat();

      const uniqueMap = new Map();

      flattened.forEach((item) => {
        const date = String(item?.date_presence || "").slice(0, 10);
        const adherentId = Number(item?.adherent_id);

        if (!date || !adherentId) return;

        uniqueMap.set(`${adherentId}-${date}`, item);
      });

      setMonthlyAttendances(Array.from(uniqueMap.values()));
    } catch (error) {
      console.error("Erreur chargement tableau mensuel :", error);
      addNotification?.(
        "Erreur lors du chargement du tableau mensuel.",
        "error",
      );
      setMonthlyAttendances([]);
    } finally {
      setLoadingMonthly(false);
    }
  }, [addNotification, groupAdherents, selectedGroupId, selectedMonth]);

  useEffect(() => {
    fetchGroups({ silent: true });
  }, [fetchGroups]);

  useEffect(() => {
    fetchGroupAdherents();
  }, [fetchGroupAdherents]);

  useEffect(() => {
    fetchAttendanceForDay();
  }, [fetchAttendanceForDay, groupAdherentIdsKey]);

  useEffect(() => {
    fetchAttendanceForMonth();
  }, [fetchAttendanceForMonth, groupAdherentIdsKey]);

  const birthdaySet = useMemo(() => {
    return new Set(
      groupAdherents
        .filter((adherent) => isBirthdayToday(adherent, selectedDate))
        .map((adherent) => adherent.id),
    );
  }, [groupAdherents, selectedDate]);

  const filteredAdherents = useMemo(() => {
    const query = normalizeText(filters.search);

    let list = groupAdherents.filter((adherent) => {
      const status = attendance?.[adherent.id] ?? 0;

      const matchesSearch =
        !query ||
        normalizeText(
          [
            adherent?.nom,
            adherent?.prenom,
            adherent?.licence_number,
            adherent?.club,
          ]
            .filter(Boolean)
            .join(" "),
        ).includes(query);

      const matchesPresent = !filters.onlyPresent || status === 1;
      const matchesAbsent = !filters.onlyAbsent || status === 2;
      const matchesBirthday =
        !filters.onlyBirthday || birthdaySet.has(adherent.id);

      return (
        matchesSearch && matchesPresent && matchesAbsent && matchesBirthday
      );
    });

    if (filters.sortAssiduity) {
      list = list.slice().sort((a, b) => {
        const valueA = Number(assiduityInfo[a.id]?.assiduity || 0);
        const valueB = Number(assiduityInfo[b.id]?.assiduity || 0);

        return valueB - valueA;
      });
    } else {
      list = sortAdherentsByName(list);
    }

    return list;
  }, [
    assiduityInfo,
    attendance,
    birthdaySet,
    filters.onlyAbsent,
    filters.onlyBirthday,
    filters.onlyPresent,
    filters.search,
    filters.sortAssiduity,
    groupAdherents,
  ]);

  const stats = useMemo(() => {
    let present = 0;
    let absent = 0;
    let neutral = 0;

    filteredAdherents.forEach((adherent) => {
      const value = attendance?.[adherent.id] ?? 0;

      if (value === 1) present += 1;
      else if (value === 2) absent += 1;
      else neutral += 1;
    });

    const total = filteredAdherents.length;
    const filled = present + absent;
    const completion = total > 0 ? Math.round((filled / total) * 100) : 0;

    const availableJudokas = groups.reduce((sum, group) => {
      return sum + getGroupAdherentsCount(group, groupCounts);
    }, 0);

    return {
      total,
      availableJudokas,
      present,
      absent,
      neutral,
      filled,
      completion,
      birthday: filteredAdherents.filter((adherent) =>
        birthdaySet.has(adherent.id),
      ).length,
      groups: groups.length,
    };
  }, [attendance, birthdaySet, filteredAdherents, groups, groupCounts]);

  const monthlyKpis = useMemo(() => {
    const presents = monthlyRows.reduce(
      (sum, row) => sum + Number(row.totalPresent || 0),
      0,
    );

    const absents = monthlyRows.reduce(
      (sum, row) => sum + Number(row.totalAbsent || 0),
      0,
    );

    const totalMarked = presents + absents;
    const rate =
      totalMarked > 0 ? Math.round((presents / totalMarked) * 100) : 0;

    const bestPresent = monthlyRows.length
      ? Math.max(...monthlyRows.map((row) => Number(row.totalPresent || 0)))
      : 0;

    const bestCount =
      bestPresent > 0
        ? monthlyRows.filter(
            (row) => Number(row.totalPresent || 0) === bestPresent,
          ).length
        : 0;

    return {
      adherents: monthlyRows.length,
      presents,
      absents,
      totalMarked,
      rate,
      bestPresent,
      bestCount,
    };
  }, [monthlyRows]);

  const monthlyHighlightIds = useMemo(() => {
    if (!monthlyRows.length) return [];

    const maxPresent = Math.max(
      ...monthlyRows.map((row) => Number(row.totalPresent || 0)),
    );

    if (maxPresent <= 0) return [];

    return monthlyRows
      .filter((row) => Number(row.totalPresent || 0) === maxPresent)
      .map((row) => row.id);
  }, [monthlyRows]);

  const selectGroup = useCallback((groupId) => {
    setGroupAdherents([]);
    setAttendance({});
    setMonthlyAttendances([]);
    attendanceRef.current = {};
    setAssiduityInfo({});
    assiduityInfoRef.current = {};

    setFiltersState((previous) => ({
      ...previous,
      selectedGroupId: String(groupId || ""),
      search: "",
      onlyPresent: false,
      onlyAbsent: false,
      onlyBirthday: false,
      sortAssiduity: false,
    }));
  }, []);

  const changeGroup = useCallback(() => {
    setGroupAdherents([]);
    setAttendance({});
    setMonthlyAttendances([]);
    attendanceRef.current = {};
    setAssiduityInfo({});
    assiduityInfoRef.current = {};

    setFiltersState((previous) => ({
      ...previous,
      selectedGroupId: "",
      search: "",
      onlyPresent: false,
      onlyAbsent: false,
      onlyBirthday: false,
      sortAssiduity: false,
    }));
  }, []);

  const refresh = useCallback(async () => {
    await fetchGroups({ silent: false });
    await fetchGroupAdherents();
    await fetchAttendanceForDay();
    await fetchAttendanceForMonth();
  }, [
    fetchAttendanceForDay,
    fetchAttendanceForMonth,
    fetchGroupAdherents,
    fetchGroups,
  ]);

  const handleToggleAttendance = useCallback(
    async (adherentId) => {
      if (
        !selectedGroupId ||
        loadingAttendance ||
        savingIds.has(String(adherentId))
      ) {
        return;
      }

      const previousValue = attendanceRef.current[adherentId] ?? 0;
      const nextValue = previousValue === 0 ? 1 : previousValue === 1 ? 2 : 0;

      setAttendance((previous) => {
        const next = {
          ...previous,
          [adherentId]: nextValue,
        };

        attendanceRef.current = next;

        return next;
      });

      replaceMonthlyCell({
        dateISO: selectedDate,
        adherentId,
        statusValue: nextValue,
      });

      try {
        setSavingForId(adherentId, true);

        if (nextValue === 1) {
          await setOneAttendance({
            groupId: selectedGroupId,
            adherentId,
            date: selectedDate,
            presence: true,
          });
        } else if (nextValue === 2) {
          await setOneAttendance({
            groupId: selectedGroupId,
            adherentId,
            date: selectedDate,
            presence: false,
          });
        } else {
          await clearOneAttendance({
            groupId: selectedGroupId,
            adherentId,
            date: selectedDate,
          });
        }
      } catch (error) {
        console.error("Erreur mise à jour présence :", error);

        setAttendance((previous) => {
          const next = {
            ...previous,
            [adherentId]: previousValue,
          };

          attendanceRef.current = next;

          return next;
        });

        replaceMonthlyCell({
          dateISO: selectedDate,
          adherentId,
          statusValue: previousValue,
        });

        addNotification?.("Échec de la mise à jour de la présence.", "error");
      } finally {
        setSavingForId(adherentId, false);
      }
    },
    [
      addNotification,
      loadingAttendance,
      replaceMonthlyCell,
      savingIds,
      selectedDate,
      selectedGroupId,
      setSavingForId,
    ],
  );

  const handleToggleMonthCell = useCallback(
    async (adherentId, day, currentValue) => {
      if (!selectedGroupId || !selectedMonth) return;

      const dateISO = `${selectedMonth}-${day}`;
      const previousStatus =
        currentValue === true ? 1 : currentValue === false ? 2 : 0;

      const nextStatus =
        currentValue === true ? 2 : currentValue === false ? 0 : 1;

      const previousMonthlyAttendances = monthlyAttendances;
      const previousDailyValue = attendanceRef.current[adherentId] ?? 0;

      setSavingForMonthCell(adherentId, day, true);

      setMonthlyAttendances((previous) =>
        replaceMonthlyCellInList(previous, {
          selectedGroupId,
          adherentId,
          dateISO,
          statusValue: nextStatus,
        }),
      );

      if (dateISO === selectedDate) {
        setAttendance((previous) => {
          const next = {
            ...previous,
            [adherentId]: nextStatus,
          };

          attendanceRef.current = next;

          return next;
        });
      }

      try {
        if (nextStatus === 1) {
          await setOneAttendance({
            groupId: selectedGroupId,
            adherentId,
            date: dateISO,
            presence: true,
          });
        } else if (nextStatus === 2) {
          await setOneAttendance({
            groupId: selectedGroupId,
            adherentId,
            date: dateISO,
            presence: false,
          });
        } else {
          await clearOneAttendance({
            groupId: selectedGroupId,
            adherentId,
            date: dateISO,
          });
        }
      } catch (error) {
        console.error("Erreur mise à jour cellule mensuelle :", error);

        setMonthlyAttendances(previousMonthlyAttendances);

        if (dateISO === selectedDate) {
          setAttendance((previous) => {
            const next = {
              ...previous,
              [adherentId]: previousDailyValue || previousStatus,
            };

            attendanceRef.current = next;

            return next;
          });
        }

        addNotification?.("Échec de la mise à jour de la case.", "error");
      } finally {
        setSavingForMonthCell(adherentId, day, false);
      }
    },
    [
      addNotification,
      monthlyAttendances,
      selectedDate,
      selectedGroupId,
      selectedMonth,
      setSavingForMonthCell,
    ],
  );

  const loadAssiduity = useCallback(
    async (ids = []) => {
      if (!selectedGroupId) return;

      const uniqueIds = Array.from(new Set(ids.filter(Boolean).map(Number)));

      const idsToLoad = uniqueIds.filter((id) => !assiduityInfoRef.current[id]);

      if (!idsToLoad.length) return;

      const results = await Promise.all(
        idsToLoad.map((id) =>
          calculateAssiduity(id, { groupId: selectedGroupId })
            .then((data) => ({ id, data }))
            .catch(() => ({ id, data: null })),
        ),
      );

      setAssiduityInfo((previous) => {
        const next = { ...previous };

        results.forEach(({ id, data }) => {
          if (data) next[id] = data;
        });

        assiduityInfoRef.current = next;

        return next;
      });
    },
    [selectedGroupId],
  );

  const loading = loadingGroups || loadingAdherents || loadingAttendance;

  return {
    roleId,
    authorized,
    groupCounts,

    groups,
    selectedGroup,
    groupAdherents,
    filteredAdherents,

    filters,
    setFilters,
    selectGroup,
    changeGroup,

    attendance,
    assiduityInfo,
    birthdaySet,
    stats,

    loading,
    loadingGroups,
    loadingAdherents,
    loadingAttendance,
    refreshing,
    savingIds,

    monthly: {
      selectedMonth,
      setSelectedMonth,
      attendances: monthlyAttendances,
      rows: monthlyRows,
      kpis: monthlyKpis,
      highlightIds: monthlyHighlightIds,
      loading: loadingMonthly,
      savingCells: savingMonthCells,
      handleToggleCell: handleToggleMonthCell,
      refresh: fetchAttendanceForMonth,
    },

    refresh,
    handleToggleAttendance,
    loadAssiduity,
  };
}

export default useAttendanceDashboard;